/**
 * The store factory — one configured RTK store per session (architecture.md §8).
 *
 * @remarks
 * Three slices: what the shell was last shown (`session`), the input line
 * (`commandUi`), and the player's preferences. The play host rides as the
 * thunk extra: the store never holds a bundle or a runtime state, because
 * in `play: 'server'` mode there is none to hold — and in `play: 'client'`
 * mode the local host holds it, with its autosave (D156).
 *
 * @packageDocumentation
 */

import type { PlayHost } from '@orpheus/player-host';

import { configureStore } from '@reduxjs/toolkit';

import { commandUiSlice } from './command-ui/command-ui-slice.js';
import { playerPreferencesSlice } from './player-preferences/player-preferences-slice.js';
import { sessionSlice } from './session/session-slice.js';

/** What every thunk is handed beside dispatch/getState. */
export interface GameStoreThunkExtra {
	readonly host: PlayHost;
}

export interface CreateGameStoreOptions {
	/** The host that runs the game — here, or on the platform. */
	readonly host: PlayHost;
	/**
	 * D132: whether to expose the store to the Redux DevTools extension.
	 * OFF unless the host says otherwise — RTK's default is on, and in client
	 * mode the view still carries every line the player has been shown. The
	 * shell passes its build mode; nothing else should.
	 */
	readonly isDevToolsEnabled?: boolean;
}

/**
 * Create the store for one session.
 *
 * @param options - {@link CreateGameStoreOptions}
 * @returns The configured store
 */
export const createGameStore = (options: CreateGameStoreOptions) =>
	configureStore({
		devTools: options.isDevToolsEnabled === true,
		middleware: (getDefaultMiddleware) => getDefaultMiddleware({ thunk: { extraArgument: { host: options.host } satisfies GameStoreThunkExtra }}),
		reducer: {
			commandUi: commandUiSlice.reducer,
			playerPreferences: playerPreferencesSlice.reducer,
			session: sessionSlice.reducer,
		},
	});

export type GameStore = ReturnType<typeof createGameStore>;

export type GameStoreState = ReturnType<GameStore['getState']>;

export type GameStoreDispatch = GameStore['dispatch'];
